import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UpdateDepositFormView } from 'components/UpdateDepositForm/UpdateDepositFormView';
import { useApp, useUpdateDeposit } from 'providers';
import { useFormik } from 'formik';
import strings from 'utils/strings';
import { getVehicleExterior, getVehicleInterior } from 'utils/vehicleBody';
import { updateVehicleValidationSchema } from './validationSchema';
import {
  createAddressObject,
  createCivilityObject,
  createPhoneObject,
  undefinedIfEmpty,
  validateVehiclePrice,
} from 'utils';
import { validationSchema as contactValidationSchema } from 'components/ContactInformationForm/validationSchema';
import { validationSchema as contactProValidationSchema } from 'components/ContactInformationProForm/validationSchema';
import {
  ContactInfoProResponse,
  User,
  ValidationResponse,
  UpdateClassifiedRequest,
  VehicleConditionOptions,
  UpdateDepositValues,
  VehicleDescriptionItem,
  AwsPicture,
  ApiError,
} from 'types';
import {
  AuthenticationService,
  DepositPicturesService,
  NormalizedValidationService,
  PhoneValidationService,
  Tracking,
  UserManagementService,
} from 'api';
import { ROUTES } from 'routes';
import { useHistory } from 'react-router-dom';
import { NetworkWarranty } from '@groupelacentrale/classified-core';
import { isImported } from 'utils/vehicleVersion';

const isPart = (userData: User | ContactInfoProResponse): userData is User => {
  return (userData as User).id !== undefined;
};

const isPro = (userData: User | ContactInfoProResponse): userData is ContactInfoProResponse => {
  return (userData as ContactInfoProResponse).emails !== undefined;
};

const getNetworkWarranty = (values: UpdateDepositValues): NetworkWarranty | undefined =>
  values.duration || (values.designation && Object.keys(values.designation).length > 0)
    ? { duration: values.duration, designation: values.designation }
    : undefined;

export const UpdateDepositForm: React.FC = () => {
  const [initial, setInitial] = useState(0);
  const { user } = useApp();
  const history = useHistory();
  const {
    classified,
    userData,
    updateClassified,
    labelos,
    updateValues,
    phoneRequestId,
    uploadPhotos,
    pictures = [],
    handleReturn,
    type,
  } = useUpdateDeposit();
  const [errorMessage, setErrorMessage] = useState('');

  const { seatMaterial, internalColor } = getVehicleInterior(classified!.vehicle.internalColor!);
  const { paintType, externalColor } = getVehicleExterior(classified!.vehicle.externalColor!);
  const address = (isPart(userData!) && userData.addresses && userData.addresses[0]) || undefined;
  const userPhone = useMemo(
    () => (isPart(userData!) && userData.phones ? userData.phones.find(phone => phone.type === 'SELLER') : undefined),
    [userData],
  );

  const updatePhotos = useCallback(
    async (files: (string | File)[]) => {
      const keepPictures: AwsPicture[] = [];
      const newFiles: File[] = [];
      for (const file of files) {
        if (file instanceof File) {
          newFiles.push(file);
        } else {
          const picture = pictures.find(p => p.url === file);
          if (picture) {
            keepPictures.push({
              action: 'KEEP',
              type: 'STANDARD',
              position: picture.position!,
            });
          }
        }
      }
      const newPictures: AwsPicture[] = await DepositPicturesService.upload(newFiles, false, classified!.reference!);
      await uploadPhotos(classified!, keepPictures.concat(newPictures));
    },
    [classified, pictures, uploadPhotos],
  );

  const initialValues: UpdateDepositValues = {
    firstHand: classified!.vehicle.firstHand!,
    mileage: classified!.vehicle.mileage!,
    imported: isImported(classified!.vehicle.origin),
    condition: classified!.vehicle.condition
      ? VehicleConditionOptions.find(c => strings.normalize(c) === strings.normalize(classified!.vehicle.condition))
      : undefined,
    externalColor,
    paintType,
    internalColor,
    seatMaterial,
    vehiclePrice: classified!.price!,
    firstName: classified!.firstname!,
    lastName: classified!.lastname!,
    email: classified!.contacts.email!,
    phone: classified!.contacts.phone1!.value,
    contact: classified!.contacts.phone1!.note,
    phone2: classified!.contacts.phone2?.value,
    contact2: classified!.contacts.phone2?.note,
    zipCode: classified!.zipCode!,
    referencePro: classified!.referencePro,
    civility: (isPart(userData!) && (userData.civility?.abbr as string | undefined)) || undefined,
    city: address?.city || '',
    country: address?.country || 'FR',
    latitude: address?.geoLoc?.latitude || 0,
    longitude: address?.geoLoc?.longitude || 0,
    address:
      [address?.addressLine1, address?.addressLine2, address?.addressLine3, address?.addressLine4].join('') || '',
    equipments: classified!.vehicle.equipments,
    packs: classified!.vehicle.packs,
    options: classified!.vehicle.options,
    repairs: classified!.vehicle.repairs,
    duration: classified!.networkWarranty?.duration,
    designation: classified!.networkWarranty?.designation,
    files: pictures.map(p => p.url!),
  };

  const formik = useFormik({
    initialValues,
    validationSchema: updateVehicleValidationSchema[type!].concat(
      user!.isPro ? contactProValidationSchema : contactValidationSchema,
    ),
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      const errors = {
        ...validateVehiclePrice(values.vehiclePrice),
        // Valider les autres champs
      };

      if (Object.keys(errors).length !== 0) {
        setErrors(errors);
        Tracking.sendCurrentPageError();
        setSubmitting(false);
        return;
      }

      const updateData: UpdateClassifiedRequest = {
        price: values.vehiclePrice,
        vehicle: {
          imported: values.imported,
          firstHand: values.firstHand,
          mileage: values.mileage,
          ...(!['MOTO', 'CAMPING-CAR', 'CARAVANE'].includes(type || '') && {
            internalColor: `${values.seatMaterial} ${values.internalColor}`,
          }),
          externalColor: `${values.externalColor} ${values.paintType}`,
          condition: values.condition,
          options: values.options
            .filter(e => e.selected)
            .map((option: VehicleDescriptionItem) => ({
              name: option.label,
            })),
          equipments: values.equipments.filter(e => e.selected),
          // @ts-ignore
          packs: values.packs
            .filter(e => e.selected)
            .map((pack: VehicleDescriptionItem) => ({
              details: [{ name: pack.label }],
            })),
          repairs: values.repairs.filter(e => e.selected).map(r => r.label),
        },
        firstname: values.firstName,
        lastname: values.lastName,
        zipCode: values.zipCode,
        contacts: {
          email: values.email,
          phone1: {
            value: values.phone,
            note: values.contact,
          },
          phone2:
            values.phone2 && values.phone2.trim().length > 0
              ? { value: values.phone2, note: undefinedIfEmpty(values.contact2) }
              : null,
        },
        referencePro: values.referencePro,
        networkWarranty: getNetworkWarranty(values),
        // REMINDER : Dont forget to add new fields in phoneRequestId useEffect also
      };

      if (!user!.isPro) {
        const userInfo: Partial<User> = {};
        let phoneValidationResponse: ValidationResponse | undefined;

        if (values.firstName !== initialValues.firstName) {
          userInfo.firstName = values.firstName;
        }

        if (values.lastName !== initialValues.lastName) {
          userInfo.lastName = values.lastName;
        }

        if (values.civility !== initialValues.civility) {
          userInfo.civility = createCivilityObject(values.civility);
        }

        if (values.phone !== initialValues.phone) {
          let phones = (isPart(userData!) && userData.phones) || [];
          try {
            phoneValidationResponse = await NormalizedValidationService.validatePhoneNumber(values.phone);
            if (userPhone) {
              const index = phones.findIndex(p => p.number === userPhone.number);
              phones![index!].number = phoneValidationResponse.nationalPhoneNumber;
              phones![index!].validationDate = '';
            } else {
              phones = [createPhoneObject(phoneValidationResponse.nationalPhoneNumber, 'SELLER', '')].concat(phones);
            }
            userInfo.phones = phones;
          } catch (e) {
            setErrorMessage((e as ApiError).message);
            Tracking.sendCurrentPageError();
            setSubmitting(false);
            return;
          }
        }

        if (
          values.address !== initialValues.address ||
          values.city !== initialValues.city ||
          values.country !== initialValues.country ||
          values.zipCode !== initialValues.zipCode
        ) {
          const newAddress = createAddressObject(
            values.address,
            values.zipCode,
            values.city,
            values.country,
            { latitude: values.latitude, longitude: values.longitude },
            address?.type || 'DEFAULT',
            address?.name || '',
          );
          if (address) {
            userInfo.addresses = [...(user!.addresses || [])];
            userInfo.addresses[0] = newAddress;
          } else {
            userInfo.addresses = [newAddress].concat(user!.addresses || []);
          }
        }

        if (Object.keys(userInfo).length !== 0) {
          try {
            await UserManagementService.updateUser(user!.id, userInfo);
            if (userInfo.phones) {
              const requestId = await PhoneValidationService.requestCode(
                phoneValidationResponse!.internationalPhoneNumber,
                user!.userCorrelationId,
                phoneValidationResponse!.lineType,
              );
              history.push(ROUTES.phoneValidationUpdate(classified!.reference, 'modification'), {
                requestId,
                internationalPhoneNumber: phoneValidationResponse!.internationalPhoneNumber,
                nationalPhoneNumber: phoneValidationResponse!.nationalPhoneNumber,
                lineType: phoneValidationResponse!.lineType,
                updateValues: values,
              });
              return;
            }
          } catch (e) {
            setErrorMessage((e as ApiError).message);
            Tracking.sendCurrentPageError();
            setSubmitting(false);
            return;
          }
        }
      }

      // Update photos
      await AuthenticationService.refresh();
      await updatePhotos(values.files);

      return updateClassified(updateData)
        .catch(r => {
          if (r.code === 'REF_PRO_DUPLICATE') {
            //errors.referencePro = 'La référence pro est déjà utilisée';
            setErrors({ referencePro: 'La référence pro est déjà utilisée' });
          } else {
            setErrorMessage(r.message);
          }
          Tracking.sendCurrentPageError();
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  const versionUpdatedFields = useMemo(
    () => ({
      imported: formik.initialValues.imported !== formik.values.imported,
      firstHand: formik.initialValues.firstHand !== formik.values.firstHand,
      mileage: formik.initialValues.mileage !== formik.values.mileage,
      condition: formik.initialValues.condition !== formik.values.condition,
      externalColor: formik.initialValues.externalColor !== formik.values.externalColor,
      paintType: formik.initialValues.paintType !== formik.values.paintType,
      internalColor: type !== 'MOTO' ? formik.initialValues.internalColor !== formik.values.internalColor : false,
      seatMaterial: type !== 'MOTO' ? formik.initialValues.seatMaterial !== formik.values.seatMaterial : false,
      referencePro: formik.initialValues.referencePro !== formik.values.referencePro,
    }),
    [formik.values, formik.initialValues, type],
  );

  const contactUpdatedFields = useMemo(
    () => ({
      civility: formik.values.civility !== formik.initialValues.civility,
      firstName: formik.values.firstName !== formik.initialValues.firstName,
      lastName: formik.values.lastName !== formik.initialValues.lastName,
      city: formik.values.city !== formik.initialValues.city,
      country: formik.values.country !== formik.initialValues.country,
      address: formik.values.address !== formik.initialValues.address,
      zipCode: formik.values.zipCode !== formik.initialValues.zipCode,
      phone: formik.values.phone !== formik.initialValues.phone,
      contact: formik.values.contact !== formik.initialValues.contact,
      phone2: formik.values.phone2 !== formik.initialValues.phone2,
      contact2: formik.values.contact2 !== formik.initialValues.contact2,
      email: formik.values.email !== formik.initialValues.email,
    }),
    [formik.values, formik.initialValues],
  );

  useEffect(() => {
    if (updateValues && phoneRequestId && initial === 0) {
      setInitial(i => i + 1);
      formik.setValues(updateValues);
      formik.setSubmitting(true);
      (async () => {
        try {
          if (updateValues.files) {
            await updatePhotos(updateValues.files);
          }
          await updateClassified({
            price: updateValues.vehiclePrice,
            vehicle: {
              imported: updateValues.imported,
              firstHand: updateValues.firstHand,
              mileage: updateValues.mileage,
              ...(!['MOTO', 'CAMPING-CAR', 'CARAVANE'].includes(type || '') && {
                internalColor: `${updateValues.seatMaterial} ${updateValues.internalColor}`,
              }),
              externalColor: `${updateValues.externalColor} ${updateValues.paintType}`,
              condition: updateValues.condition,
              options: updateValues.options
                .filter(e => e.selected)
                .map((option: VehicleDescriptionItem) => ({
                  name: option.label,
                })),
              equipments: updateValues.equipments.filter(e => e.selected),
              // @ts-ignore
              packs: updateValues.packs
                .filter(e => e.selected)
                .map((pack: VehicleDescriptionItem) => ({
                  details: [{ name: pack.label }],
                })),
              repairs: updateValues.repairs.filter(e => e.selected).map(r => r.label),
            },
            firstname: updateValues.firstName,
            lastname: updateValues.lastName,
            zipCode: updateValues.zipCode,
            contacts: {
              email: updateValues.email,
              phone1: {
                value: updateValues.phone,
                note: updateValues.contact,
                requestId: phoneRequestId,
              },
              phone2:
                updateValues.phone2 && updateValues.phone2.trim().length > 0
                  ? { value: updateValues.phone2, note: undefinedIfEmpty(updateValues.contact2) }
                  : null,
            },
            referencePro: updateValues.referencePro,
            networkWarranty: getNetworkWarranty(updateValues),
          });
        } catch (r) {
          setErrorMessage((r as ApiError).message);
          Tracking.sendCurrentPageError();
        } finally {
          formik.setSubmitting(false);
        }
      })();
    } else if (updateValues && initial === 0) {
      setInitial(i => i + 1);
      formik.setValues(updateValues);
    }
  }, [updateValues, phoneRequestId, formik, updateClassified, initial, updatePhotos, type]);

  const descriptionUpdateFields: number = useMemo(() => {
    const equipmentsUpdated = formik.initialValues.equipments.reduce(
      (count, currentValue, currentIndex) =>
        count + (currentValue.selected === formik.values.equipments[currentIndex].selected ? 0 : 1),
      0,
    );

    const packsUpdated = formik.initialValues.packs.reduce(
      (count, currentValue, currentIndex) =>
        count + (currentValue.selected === formik.values.packs[currentIndex].selected ? 0 : 1),
      0,
    );

    const optionsUpdated = formik.initialValues.options.reduce(
      (count, currentValue, currentIndex) =>
        count + (currentValue.selected === formik.values.options[currentIndex].selected ? 0 : 1),
      0,
    );

    const repairsUpdated = formik.initialValues.repairs.reduce(
      (count, currentValue, currentIndex) =>
        count + (currentValue.selected === formik.values.repairs[currentIndex].selected ? 0 : 1),
      0,
    );

    const networkWarrantyUpdated = user!.isPro
      ? (formik.initialValues.duration === formik.values.duration ? 0 : 1) +
        (formik.initialValues.designation?.id === formik.values.designation?.id ? 0 : 1)
      : 0;

    return equipmentsUpdated + packsUpdated + optionsUpdated + repairsUpdated + networkWarrantyUpdated;
  }, [formik.initialValues, formik.values, user]);

  const picturesUpdated: number = useMemo(
    () =>
      formik.values.files.reduce(
        (count, file) =>
          count + (pictures.some(p => (file instanceof File ? p.file === file : p.url === file)) ? 0 : 1),
        0,
      ) +
      pictures.reduce(
        (count, p) => count + (formik.values.files.some(f => (f instanceof File ? f === p.file : f === p.url)) ? 0 : 1),
        0,
      ),
    [formik.values.files, pictures],
  );

  return (
    <UpdateDepositFormView
      vehicleType={type!}
      emails={(isPro(userData!) && userData.emails) || []}
      isPro={user!.isPro}
      onReturn={handleReturn}
      onSubmit={formik.handleSubmit}
      {...formik}
      errorMessage={errorMessage}
      versionUpdatedFields={versionUpdatedFields}
      contactUpdatedFields={contactUpdatedFields}
      descriptionUpdateFields={descriptionUpdateFields}
      labelos={labelos}
      picturesUpdated={picturesUpdated}
    />
  );
};
